/**
 * Composant de test pour diagnostiquer les problèmes de reconnaissance vocale
 * Utilisé pour tester la Web Speech API en développement local
 */

import React, { useState, useEffect } from 'react';

export const SpeechToTextTest: React.FC = () => {
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'online' | 'offline'>('unknown');
  const [micPermission, setMicPermission] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [testStep, setTestStep] = useState<'idle' | 'testing-mic' | 'testing-recognition'>('idle');

  // Vérifier le support et la connexion au montage
  useEffect(() => {
    // Vérifier la connexion internet
    const checkConnection = () => {
      if (navigator.onLine) {
        setConnectionStatus('online');
      } else {
        setConnectionStatus('offline');
        setError('Vous êtes hors ligne. La reconnaissance vocale nécessite une connexion internet.');
      }
    };

    checkConnection();
    window.addEventListener('online', () => setConnectionStatus('online'));
    window.addEventListener('offline', () => {
      setConnectionStatus('offline');
      setError('Connexion perdue. La reconnaissance vocale nécessite une connexion internet.');
    });

    // Vérifier le support de la Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const supported = !!SpeechRecognition;
    setIsSupported(supported);

    if (!supported) {
      setError('Web Speech API non supportée dans ce navigateur.');
    }

    // Vérifier si on est en HTTPS (requis pour certains navigateurs)
    const isHttps = window.location.protocol === 'https:';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    console.log('🔍 Diagnostic Speech-to-Text:');
    console.log('• HTTPS:', isHttps);
    console.log('• Localhost:', isLocalhost);
    console.log('• API Supportée:', supported);
    console.log('• Connexion:', navigator.onLine ? 'Online' : 'Offline');
    console.log('• User Agent:', navigator.userAgent);

    return () => {
      window.removeEventListener('online', () => setConnectionStatus('online'));
      window.removeEventListener('offline', () => setConnectionStatus('offline'));
    };
  }, []);

  // Tester les permissions du microphone
  const testMicrophonePermission = async () => {
    try {
      setTestStep('testing-mic');
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      setMicPermission(result.state);

      if (result.state === 'denied') {
        setError('Microphone bloqué. Autorisez l\'accès dans les paramètres du navigateur.');
        return false;
      }

      if (result.state === 'prompt') {
        setError('Cliquez sur "Autoriser" quand le navigateur demande l\'accès au microphone.');
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Permissions API non supportée, test manuel nécessaire');
      setMicPermission('unknown');
      return true; // Continue avec le test manuel
    }
  };

  const startListening = async () => {
    if (!isSupported) {
      setError('Web Speech API non supportée.');
      return;
    }

    if (!navigator.onLine) {
      setError('Vous êtes hors ligne. Connexion requise.');
      return;
    }

    setError(null);
    setTranscript('');

    // Tester les permissions du microphone d'abord
    const micOk = await testMicrophonePermission();
    if (!micOk) {
      return;
    }

    setTestStep('testing-recognition');

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'fr-FR';

      recognition.onstart = () => {
        setIsListening(true);
        console.log('🎤 Reconnaissance démarrée');
      };

      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        setTranscript(finalTranscript || interimTranscript);
      };

      recognition.onerror = (event: any) => {
        setIsListening(false);
        console.error('❌ Erreur reconnaissance:', event.error);

        const errorMessages: { [key: string]: string } = {
          'network': 'Erreur réseau - Vérifiez votre connexion internet',
          'not-allowed': 'Microphone non autorisé - Cliquez sur le cadenas dans la barre d\'adresse',
          'no-speech': 'Aucune parole détectée',
          'audio-capture': 'Problème de capture audio',
          'service-not-allowed': 'Service non autorisé par le navigateur',
          'aborted': 'Reconnaissance annulée',
          'language-not-supported': 'Langue non supportée'
        };

        setError(errorMessages[event.error] || `Erreur inconnue: ${event.error}`);
      };

      recognition.onend = () => {
        setIsListening(false);
        console.log('🎤 Reconnaissance terminée');
      };

      recognition.start();
    } catch (error) {
      console.error('❌ Erreur lors du démarrage:', error);
      setError('Erreur lors du démarrage de la reconnaissance vocale');
    }
  };

  const stopListening = () => {
    setIsListening(false);
  };

  return (
    <div className="p-6 max-w-md mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">🧪 Test Speech-to-Text</h2>

      {/* Diagnostic */}
      <div className="mb-4 p-3 bg-gray-50 rounded">
        <h3 className="font-semibold mb-2">🔍 Diagnostic:</h3>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span>API Supportée:</span>
            <span className={isSupported ? 'text-green-600' : 'text-red-600'}>
              {isSupported === null ? '...' : isSupported ? '✅' : '❌'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Connexion:</span>
            <span className={connectionStatus === 'online' ? 'text-green-600' : 'text-red-600'}>
              {connectionStatus === 'online' ? '✅ Online' : connectionStatus === 'offline' ? '❌ Offline' : '...'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>HTTPS:</span>
            <span className={window.location.protocol === 'https:' ? 'text-green-600' : 'text-yellow-600'}>
              {window.location.protocol === 'https:' ? '✅' : '⚠️ HTTP'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Localhost:</span>
            <span className={window.location.hostname === 'localhost' ? 'text-yellow-600' : 'text-green-600'}>
              {window.location.hostname === 'localhost' ? '⚠️ Oui' : '✅ Non'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Microphone:</span>
            <span className={
              micPermission === 'granted' ? 'text-green-600' :
              micPermission === 'denied' ? 'text-red-600' :
              micPermission === 'prompt' ? 'text-yellow-600' :
              'text-gray-500'
            }>
              {micPermission === 'granted' ? '✅ Autorisé' :
               micPermission === 'denied' ? '❌ Bloqué' :
               micPermission === 'prompt' ? '⚠️ À autoriser' :
               '❓ Inconnu'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Étape test:</span>
            <span className="text-blue-600">
              {testStep === 'idle' ? '🏠 Prêt' :
               testStep === 'testing-mic' ? '🎤 Test micro' :
               testStep === 'testing-recognition' ? '🎧 Test reconnaissance' :
               'Inconnu'}
            </span>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="mb-4 space-y-2">
        <button
          onClick={() => testMicrophonePermission()}
          className="w-full py-2 px-4 rounded-lg font-medium bg-purple-500 text-white hover:bg-purple-600 transition-all"
        >
          🔍 Tester le microphone seulement
        </button>

        <button
          onClick={isListening ? stopListening : startListening}
          disabled={!isSupported || connectionStatus === 'offline'}
          className={`
            w-full py-3 px-4 rounded-lg font-medium transition-all
            ${isListening
              ? 'bg-red-500 text-white animate-pulse'
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            ${(!isSupported || connectionStatus === 'offline')
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
            }
          `}
        >
          {isListening ? '⏹️ Arrêter' : '🎤 Test complet (micro + reconnaissance)'}
        </button>
      </div>

      {/* Résultat */}
      {transcript && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
          <h3 className="font-semibold text-green-800 mb-1">📝 Transcription:</h3>
          <p className="text-green-700">{transcript}</p>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <h3 className="font-semibold text-red-800 mb-1">❌ Erreur:</h3>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Conseils */}
      <div className="text-xs text-gray-600 space-y-1">
        <p><strong>🔧 Solutions selon votre situation:</strong></p>

        {window.location.hostname === 'localhost' ? (
          <>
            <p><strong>📍 Développement local:</strong></p>
            <p>• HTTPS requis: Configurez HTTPS dans vite.config.ts</p>
            <p>• Alternative: <code>npm install -g serve && serve -s dist -l 3000</code></p>
          </>
        ) : (
          <>
            <p><strong>🌐 Production:</strong></p>
            <p>• Vérifiez que HTTPS est bien activé sur votre domaine</p>
            <p>• Testez sur différents navigateurs (Chrome recommandé)</p>
          </>
        )}

        <p><strong>🎤 Microphone:</strong></p>
        <p>• Cliquez sur le 🔒 dans la barre d'adresse</p>
        <p>• Réglez "Microphone" sur "Autoriser"</p>
        <p>• Rafraîchissez la page après changement</p>

        <p><strong>🌍 Navigateurs supportés:</strong></p>
        <p>• ✅ Chrome/Chromium (meilleur support)</p>
        <p>• ✅ Edge (bon support)</p>
        <p>• ⚠️ Firefox (support limité)</p>
        <p>• ❌ Safari (non supporté)</p>
      </div>
    </div>
  );
};
